import './AgeVerificationModal.scss';

import { Location, navigate } from '@reach/router';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

import ARROW_ICON from '../../assets/images/AgeVerification/age-verification-arrow.svg';
import BADGE_ICON from '../../assets/images/AgeVerification/age-verification-badge.svg';
import CREDIT_CARD_ICON from '../../assets/images/AgeVerification/age-verification-credit-card.svg';
import CREDITS_ICON from '../../assets/images/AgeVerification/age-verification-credits.svg';
import ID_ICON from '../../assets/images/AgeVerification/age-verification-id-card.svg';
import SELFIE_ICON from '../../assets/images/AgeVerification/age-verification-selfie.svg';
import LOGO from '../../assets/images/LandingPage/LocalFlirt-red-logo-black.svg';
import { key as USER_DATA_KEY, useGetUserData } from '../../core/APIHooks/userData/useGetUserData';
import { useStartYotiVerification } from '../../core/APIHooks/yoti/useStartYotiVerification';
import { MEMBERS_ROUTES } from '../../pages/MainView/components/MembersAreaMain/members_routes';
import { getToken, hasTokenExpired } from '../../services/token-service/token.service';
import { YotiVerificationMethod } from '../../services/yoti-service/yoti.service';
import { getAgeVerificationState } from '../../utils/ageVerification';
import sha1 from '../../utils/sha1';
import Button from '../Button/Button';

enum StepCreditCardEnum {
  CLOSED = 'CLOSED',
  INTRO = 'INTRO',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}

type LoadingMethod = YotiVerificationMethod | 'credits';

type VerificationOption = {
  method: LoadingMethod;
  image: string;
  title: string;
  description: string;
  onClick: () => void;
};

type RouterLocation = { pathname: string; search: string };

const CREDITS_PATH = `${MEMBERS_ROUTES.MEMBERSAREA}${MEMBERS_ROUTES.CREDITS}`;
const SUCCESS_AUTOCLOSE_MS = 2000;
const MODAL_Z_INDEX = 10000;

const AgeVerificationModalInner = ({ location }: { location: RouterLocation }) => {
  const queryClient = useQueryClient();

  const { data } = useGetUserData();
  const user = data?.data;
  const { mutate: startVerification } = useStartYotiVerification();

  // clean users on some brands only get the two native Yoti methods (selfie + ID)
  const onlySelfieAndId = user?.use_only_yoti_verification_selfie_and_id ?? false;

  const [step, setStep] = useState<StepCreditCardEnum>(StepCreditCardEnum.CLOSED);
  const [loadingMethod, setLoadingMethod] = useState<LoadingMethod | null>(null);
  const hasHandledYotiParam = useRef(false);

  const isMembersAreaRoute = location.pathname.startsWith(MEMBERS_ROUTES.MEMBERSAREA);
  const isAuthenticated = Boolean(getToken()) && !hasTokenExpired();
  const restricted =
    isMembersAreaRoute && isAuthenticated && getAgeVerificationState(user).hasFullAgeRestriction;
  const isCreditsPage = location.pathname.includes(MEMBERS_ROUTES.CREDITS);
  const yotiStatusParam = new URLSearchParams(location.search).get('yoti');

  // selfie + ID users can't buy credits, so keep the modal open even on /credits
  useEffect(() => {
    if (yotiStatusParam) return; // handled by the redirect effect below
    if (!restricted) return;
    const exemptOnCreditsPage = isCreditsPage && !onlySelfieAndId;
    setStep(exemptOnCreditsPage ? StepCreditCardEnum.CLOSED : StepCreditCardEnum.INTRO);
  }, [restricted, isCreditsPage, onlySelfieAndId, yotiStatusParam]);

  // Handle the ?yoti=success|failed redirect coming back from Yoti
  useEffect(() => {
    if (!yotiStatusParam || hasHandledYotiParam.current) return;
    hasHandledYotiParam.current = true;

    const params = new URLSearchParams(location.search);
    params.delete('yoti');
    const query = params.toString();
    navigate(query ? `${location.pathname}?${query}` : location.pathname, {
      replace: true
    });

    if (yotiStatusParam === 'success') {
      setStep(StepCreditCardEnum.SUCCESS);
      queryClient.invalidateQueries(USER_DATA_KEY);
    } else {
      setStep(StepCreditCardEnum.ERROR);
    }
  }, [yotiStatusParam, location.pathname, location.search, queryClient]);

  useEffect(() => {
    if (step !== StepCreditCardEnum.SUCCESS) return undefined;
    const timer = setTimeout(() => setStep(StepCreditCardEnum.CLOSED), SUCCESS_AUTOCLOSE_MS);
    return () => clearTimeout(timer);
  }, [step]);

  useEffect(() => {
    setLoadingMethod(null);
  }, [location.pathname]);

  const startYotiFlow = useCallback(
    async (method: YotiVerificationMethod) => {
      if (!user?.id || !user?.email || loadingMethod) return;

      try {
        setLoadingMethod(method);
        const hash = await sha1(user.email);

        startVerification(
          { user_id: user.id, hash, method },
          {
            onSuccess: (response) => {
              if (response?.verification_url) {
                window.location.href = response.verification_url;
              } else {
                setLoadingMethod(null);
                setStep(StepCreditCardEnum.ERROR);
              }
            },
            onError: () => {
              setLoadingMethod(null);
              setStep(StepCreditCardEnum.ERROR);
            }
          }
        );
      } catch {
        setLoadingMethod(null);
        setStep(StepCreditCardEnum.ERROR);
      }
    },
    [user, loadingMethod, startVerification]
  );

  const handleBuyCredits = useCallback(() => {
    setLoadingMethod('credits');
    navigate(CREDITS_PATH);
  }, []);

  const handleSelfie = useCallback(() => startYotiFlow('selfie'), [startYotiFlow]);

  const handleCreditCard = useCallback(() => startYotiFlow('credit_card'), [startYotiFlow]);

  const handleId = useCallback(() => startYotiFlow('doc_scan'), [startYotiFlow]);

  const handleRetry = useCallback(() => setStep(StepCreditCardEnum.INTRO), []);

  if (step === StepCreditCardEnum.CLOSED) return null;

  const renderBody = () => {
    if (step === StepCreditCardEnum.SUCCESS) {
      return (
        <div className="AgeVerificationModal__state AgeVerificationModal__state--success">
          <div className="AgeVerificationModal__stateIcon">✓</div>
          <h2 className="AgeVerificationModal__successTitle">Age Verified!</h2>
          <p className="AgeVerificationModal__description">
            Thank you for verifying your age. You now have full access to the platform.
          </p>
        </div>
      );
    }

    if (step === StepCreditCardEnum.ERROR) {
      return (
        <div className="AgeVerificationModal__state AgeVerificationModal__state--error">
          <img className="AgeVerificationModal__logo" src={LOGO} alt="" />
          <div className="AgeVerificationModal__stateIcon AgeVerificationModal__stateIcon--error">
            !
          </div>
          <h2 className="AgeVerificationModal__errorTitle">Verification Failed</h2>
          <p className="AgeVerificationModal__description">
            We couldn&apos;t verify your age. This could be due to lighting or camera issues. Please
            try again.
          </p>
          <Button
            className="AgeVerificationModal__retryButton"
            text="TRY AGAIN"
            onClick={handleRetry}
          />
        </div>
      );
    }

    const selfieOption: VerificationOption = {
      method: 'selfie',
      image: SELFIE_ICON,
      title: 'Take a Selfie',
      description: 'For instant age estimation',
      onClick: handleSelfie
    };

    const options: VerificationOption[] = onlySelfieAndId
      ? [
          selfieOption,
          {
            method: 'doc_scan',
            image: ID_ICON,
            title: 'Verify with your ID',
            description: 'For exact age verification',
            onClick: handleId
          }
        ]
      : [
          {
            method: 'credits',
            image: CREDITS_ICON,
            title: 'Buy Credits',
            description: 'Purchase credits to verify',
            onClick: handleBuyCredits
          },
          selfieOption,
          {
            method: 'credit_card',
            image: CREDIT_CARD_ICON,
            title: 'Verify with Credit Card',
            description: 'For exact age verification',
            onClick: handleCreditCard
          }
        ];

    return (
      <div className="AgeVerificationModal__intro">
        <div className="AgeVerificationModal__headerRow">
          <img className="AgeVerificationModal__badge" src={BADGE_ICON} alt="" />
          <h2 className="AgeVerificationModal__title">Verify your age</h2>
        </div>
        <p className="AgeVerificationModal__description">
          This site is intended for users aged 18 and over. Please confirm your age using one of the
          options below.
        </p>
        <p className="AgeVerificationModal__description">
          Verification takes less than a minute and is handled securely. You only need to do this
          once.
        </p>

        <div
          className={`AgeVerificationModal__options${
            onlySelfieAndId ? ' AgeVerificationModal__options--two' : ''
          }`}
        >
          {options.map((option) => (
            <button
              key={option.method}
              type="button"
              className="AgeVerificationModal__option"
              onClick={option.onClick}
              disabled={loadingMethod !== null}
            >
              <img className="AgeVerificationModal__optionImage" src={option.image} alt="" />
              <div className="AgeVerificationModal__optionTitleRow">
                <span className="AgeVerificationModal__optionTitle">{option.title}</span>
                <img className="AgeVerificationModal__optionArrow" src={ARROW_ICON} alt="" />
              </div>
              <span className="AgeVerificationModal__optionDescription">{option.description}</span>
              {loadingMethod === option.method && (
                <span className="AgeVerificationModal__optionLoader">
                  <Spin />
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal
      className="AgeVerificationModal"
      rootClassName="AgeVerificationModal__root"
      zIndex={MODAL_Z_INDEX}
      open
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={680}
    >
      {renderBody()}
    </Modal>
  );
};

// wrapped in <Location> because this modal is mounted outside the app's <Router>
// so the useLocation/useNavigate hooks have no LocationProvider in their ancestry
const AgeVerificationModal = () => (
  // @ts-ignore
  <Location>{({ location }) => <AgeVerificationModalInner location={location} />}</Location>
);

export default AgeVerificationModal;
